import { sanitizeRoute } from '../../../shared/routeRetention';
import type { StoredRoute } from '../../../shared/types';
import { linkOp, reconcileDependencies } from './dependencies';
import type { OutboxMethod, OutboxOp } from './db';
import { getDb } from './db';

export interface EnqueueInput {
  method: OutboxMethod;
  path: string;
  body?: unknown;
  label: string;
}

const safeBody = (path: string, body: unknown): unknown => {
  if (!/^\/api\/daily\/routes\//.test(path) || !body || typeof body !== 'object') return body;
  const value = body as { route?: StoredRoute };
  return value.route && Array.isArray(value.route.stops) ? { ...value, route: sanitizeRoute(value.route) } : body;
};

export const buildOp = (input: EnqueueInput): OutboxOp => ({
  id: crypto.randomUUID(),
  method: input.method,
  path: input.path,
  body: safeBody(input.path, input.body),
  label: input.label,
  created_at: new Date().toISOString(),
  attempts: 0,
  status: 'pending',
});

export const enqueue = async (input: EnqueueInput): Promise<OutboxOp> => {
  const db = await getDb();
  const op = buildOp(input);
  const tx = db.transaction('outbox', 'readwrite');
  const seq = await tx.store.add(linkOp(op, await tx.store.getAll()));
  await reconcileDependencies(tx.store);
  await tx.done;
  return { ...op, seq: Number(seq) };
};

export const listOutbox = async (): Promise<OutboxOp[]> => {
  const tx = (await getDb()).transaction('outbox', 'readwrite');
  const rows = await tx.store.getAll();
  const safe = rows.map((op) => ({ ...op, body: safeBody(op.path, op.body) }));
  for (let i = 0; i < rows.length; i++)
    if (JSON.stringify(rows[i]) !== JSON.stringify(safe[i])) await tx.store.put(safe[i]!);
  await tx.done;
  return safe;
};

export const listPending = async (): Promise<OutboxOp[]> =>
  (await listOutbox()).filter((op) => op.status === 'pending');

export const listFailed = async (): Promise<OutboxOp[]> =>
  (await listOutbox()).filter((op) => op.status === 'failed');

export const countByStatus = async (): Promise<{ pending: number; failed: number; parked: number }> => {
  const db = await getDb();
  const [pending, failed, parked] = await Promise.all([
    db.countFromIndex('outbox', 'by_status', 'pending'),
    db.countFromIndex('outbox', 'by_status', 'failed'),
    db.countFromIndex('outbox', 'by_status', 'parked'),
  ]);
  return { pending, failed, parked };
};

export const removeOp = async (seq: number): Promise<void> => (await getDb()).delete('outbox', seq);

const updateOp = async (seq: number, patch: Partial<OutboxOp>): Promise<void> => {
  const db = await getDb();
  const tx = db.transaction('outbox', 'readwrite');
  const current = await tx.store.get(seq);
  if (current) await tx.store.put({ ...current, ...patch });
  await tx.done;
};

/** Transient failure: keep pending, remember the error, bump attempts. */
export const noteAttempt = (seq: number, attempts: number, error: string): Promise<void> =>
  updateOp(seq, { attempts, last_error: error });

/** Permanent non-duplicate failure (4xx): record failed so unrelated operations proceed. */
export const markFailed = (seq: number, attempts: number, error: string): Promise<void> =>
  updateOp(seq, { attempts, last_error: error, status: 'failed' });

export const retryFailed = async (): Promise<number> => {
  const failed = await listFailed();
  for (const op of failed)
    if (op.seq !== undefined) await updateOp(op.seq, { status: 'pending', last_error: undefined });
  return failed.length;
};

/** Drop parked ops; returns them so the "tap to undo" toast can put them back. */
export const discardFailed = async (): Promise<OutboxOp[]> => {
  const failed = await listFailed();
  for (const op of failed) if (op.seq !== undefined) await removeOp(op.seq);
  return failed;
};

/** Undo for discardFailed: the ops come back parked (failed) for another Retry, with new seqs. */
export const restoreOps = async (ops: readonly OutboxOp[]): Promise<void> => {
  const db = await getDb();
  const tx = db.transaction('outbox', 'readwrite');
  for (const { seq: _seq, ...op } of ops) await tx.store.add({ ...op, body: safeBody(op.path, op.body) });
  await tx.done;
};
